/* eslint-disable @stencil/strict-mutable */
import { Component, Element, Host, Prop, State, h } from '@stencil/core';

const getFullURL = (link): URL => {
  const absoluteUrlRegex = new RegExp('^(?:[a-z+]+:)?//', 'i'); // ref: https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
  if (link?.match(absoluteUrlRegex)) {
    return new URL(link);
  }
  return new URL(link, window.location.href);
};

const defaultTransform = (src: string, width) => {
  const url = getFullURL(src);
  url.searchParams.append('w', width);
  return url.href;
};

@Component({
  tag: 'sy-blog-img',
  styleUrl: 'sy-blog-img.scss',
  shadow: true,
})
export class SyBlogImg {
  private wasSizesProvided = false;

  @Element() host: HTMLSyBlogImgElement;

  @State() imgNeededWidth = 240;

  @State() hostHeight = '100%';

  /** */
  @Prop() source: string;

  /** */
  @Prop({ reflect: true }) srcset: string;

  /** */
  @Prop({ reflect: true, mutable: true }) sizes: string;

  /** */
  @Prop({ reflect: true }) alt: string;

  /** */
  @Prop() force = false;

  /** */
  @Prop() transform: (source: string, width: number) => string = defaultTransform;

  /** */
  @Prop() aspect;

  /** */
  @Prop() maxWidth;

  /** */
  @Prop() loading: 'eager' | 'lazy' = 'lazy';

  /** */
  @Prop() srcsetWidths = [240, 320, 480, 720, 960, 1200, 1600, 1900];

  private getWidthFromComputedWidth(computedWidth) {
    if (computedWidth <= 240) return 240;
    if (computedWidth <= 320) return 320;
    if (computedWidth <= 480) return 480;
    if (computedWidth <= 720) return 720;
    if (computedWidth <= 960) return 960;
    if (computedWidth <= 1200) return 1200;
    if (computedWidth <= 1600) return 1600;
    return 1900;
  }

  private getImgAttributes() {
    const attrs: any = {
      sizes: this.sizes,
      loading: this.loading,
    };

    if (this.alt) {
      attrs.alt = this.alt;
    }

    if (this.srcset) {
      attrs.srcset = this.srcset;
    }

    attrs.src = getFullURL(this.source);

    if (!this.srcset && this.transform) {
      // generate sizes using transformation
      attrs.srcset = this.srcsetWidths
        .filter((w) => (this.maxWidth && w <= this.maxWidth) || !this.maxWidth)
        .map((w) => this.transform(this.source, w) + ` ${w}w`)
        .join(', ');
    }

    if (this.force && this.transform) {
      delete attrs.sizes;
      delete attrs.srcset;

      attrs.src = this.transform(this.source, this.imgNeededWidth);
    }

    return attrs;
  }

  componentWillLoad() {
    if (this.sizes) {
      this.wasSizesProvided = true;
    }
  }

  componentDidLoad() {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === this.host) {
          this.imgNeededWidth = this.getWidthFromComputedWidth(entry.contentRect.width);
          if (!this.wasSizesProvided) {
            this.sizes = `(max-width: 750px) 720px, 1201px`;
          }
        }
      });
    });

    resizeObserver.observe(this.host);
  }

  render() {
    return <Host style={{ '--host-aspect': this.aspect }}>{this.sizes && <img {...this.getImgAttributes()} />}</Host>;
  }
}
